import { TMeasurement_issue, TUomType } from '@monorepo/types';
import { TTableCellType, TTableInfo, TTableTreeModel, TTextCell } from '../../../table.type';
import { ModifiersZTableModel } from '../../../table.modifier';
import { ExternalInputPadUtil } from '../../utils/external-input-pad-util';
import { FractionalNumber } from '@monorepo/utils';

const createHeaderRow = (): TTableInfo<any>['header']['cells'] => {
  return [
    {
      type: 'TEXT_CELL',
      text: 'DESCRIPTION',
      widthInPx: 140,
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: 'STANDARD',
      widthInPx: 72,
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: 'ACTUAL',
      widthInPx: 72,
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: 'TOL(+)',
      widthInPx: 72,
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: 'TOL(-)',
      widthInPx: 72,
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: 'DEVIATION',
      widthInPx: 72,
    },
  ];
};

const getRows = (rowItem: TMeasurement_issue, uomType: TUomType) => {
  const rows = [
    {
      type: 'TEXT_CELL',
      text: rowItem.description,
    },
    {
      type: 'TEXT_CELL',
      text: rowItem.standard,
      align: 'center',
    },
    {
      type: 'TEXT_CELL',
      text:
        uomType === 'cm'
          ? parseFloat(rowItem.standard) + parseFloat(rowItem.deviation ? rowItem.deviation : '0')
          : FractionalNumber.sumFractions(rowItem.standard, rowItem.deviation ? rowItem.deviation : '0'),
      align: 'center',
    },
    {
      type: 'TEXT_CELL',
      text: `${rowItem.tolPlus}`,
      align: 'center',
    },
    {
      type: 'TEXT_CELL',
      text: `${rowItem.tolMinus}`,
      align: 'center',
    },
    {
      type: 'TEXT_CELL',
      align: 'center',
      text: rowItem.deviation ? rowItem.deviation : '0',
      isExternalInput: {
        editStatus: rowItem.index === 0 ? true : false,
      },
    },
  ] as TTableCellType[];

  return rows;
};

export const MEASUREMENTS_LISTING_TABLE_MODEL = {
  createModel: (rowInfo: TMeasurement_issue[], uomType: TUomType) => {
    const tableModel = ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        headerConfig: createHeaderRow(),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRows(item, uomType),
        };
      },
    );
    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(tableModel);

    const initialSelectedExternalInput = tableModel.tableInfo.rows[0]?.cells[4] as TTextCell;
    const selectedFractionalValue = parseInt(initialSelectedExternalInput?.text as string);

    return {
      model: tableModel,
      currentActiveRowCellIndex: currentActiveRowCellIndex,
      selectedFractionalValue: selectedFractionalValue,
    };
  },

  updateActualAndDeviationValueBasedOnFractionalPad<T>(tableModel: TTableTreeModel<T>, value: string) {
    const updateModel = { ...tableModel };

    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(updateModel);
    if (currentActiveRowCellIndex['rowIndex'] !== -1) {
      if (value) {
        const currentDeviationValue = (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[
            currentActiveRowCellIndex['cellIndex']
          ] as TTextCell
        ).text as string;

        const newFractionalDeviationValue = FractionalNumber.sumFractions(currentDeviationValue, value);
        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[
            currentActiveRowCellIndex['cellIndex']
          ] as TTextCell
        ).text = newFractionalDeviationValue;

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).deviation = newFractionalDeviationValue;
        // Updating actual value

        const currentActualValue = (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[2] as TTextCell
        ).text as string;

        const newFractionalActualValue = FractionalNumber.sumFractions(currentActualValue, value);
        (updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[2] as TTextCell).text =
          newFractionalActualValue;

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).actual = newFractionalActualValue;
      } else {
        // Set deviation to 0 and actual equal to standard
        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[
            currentActiveRowCellIndex['cellIndex']
          ] as TTextCell
        ).text = '0';
        const standardValue = (updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[1] as TTextCell)
          .text as string;
        (updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[2] as TTextCell).text = standardValue;

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).deviation = '0';

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).actual = standardValue;
      }
    }

    return updateModel;
  },
  updateActualValueBasedOnNumberPad<T>(tableModel: TTableTreeModel<T>, value: number) {
    const updateModel = { ...tableModel };

    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(updateModel);
    if (currentActiveRowCellIndex['rowIndex'] !== -1) {
      if (value) {
        // Updating actual value

        const currentStandardValue = (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[1] as TTextCell
        ).text as string;

        let newActualValue = parseFloat(currentStandardValue) + value;
        if (!Number.isInteger(newActualValue)) {
          newActualValue = parseFloat(newActualValue.toFixed(2));
        }
        (updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[2] as TTextCell).text = newActualValue;

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).actual = newActualValue.toString();

        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).deviation = value.toString();
      } else {
        // Set actual equal to standard
        (updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[2] as TTextCell).text = (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[1] as TTextCell
        ).text;
        (
          updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].rowItem as unknown as TMeasurement_issue
        ).deviation = '0';
      }
    }

    return updateModel;
  },
};
